"use client";

import { useEffect, useId, useRef, type ReactNode } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
  /**
   * 開啟時要優先聚焦的元素。未提供時聚焦對話框本體（不會誤聚焦到危險按鈕上）。
   * 確認類對話框請指向「取消」，讓 Enter 的預設落點是安全的那一邊。
   */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** 關閉遮罩點擊關閉（執行中的破壞性操作應設 true，避免誤觸中斷） */
  disableOverlayClose?: boolean;
}

/** 可聚焦元素選擇器（排除 disabled 與 tabindex="-1"）。 */
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/*
 * K3（B3 瀏覽器稽核，低）：巢狀 Modal 的焦點歸還。
 *
 * 常見寫法是同一個父層裡放兩個互斥的 <Modal open={...}>（例如
 * course-info/page.tsx 的「須知清單」與「新增須知」，開子視窗時父視窗的 open
 * 條件變 false）。這兩個 Modal 元件實例本身不會被卸載（JSX 一直都在，只是
 * open 在 true/false 間切換），但 open=false 時 `if (!open) return null`
 * 會把該次渲染的整個 dialog DOM（含裡面的按鈕，例如「＋新增須知」）整棵拔掉，
 * 之後 open 變回 true 又整棵重建——**那顆按鈕是全新的 DOM 節點**，舊的參照
 * 已經不存在，物理上沒有辦法把焦點還原到「使用者當初點的那顆按鈕」。
 *
 * 這裡能做、也做的兩件事（不需要 admin/web 那端配合）：
 *
 *   1. restoreRef 的可靠度：兩個 <Modal> 在同一個 React commit 裡一個關閉、
 *      一個開啟時，React 對 passive effect 的順序是「這次 commit 所有 cleanup
 *      先跑完，才跑所有 setup」——關閉那個的 cleanup 會先把焦點還給*它自己*
 *      記下的觸發元素，開啟那個的 setup 才接著跑，這時 document.activeElement
 *      已經被前者的還原動作改寫過，不是「使用者真正操作的東西」。加一道判斷：
 *      新抓到的 activeElement 若是 document.body 或落在*任一* dialog 裡面
 *      （代表它是別的 Modal 剛做的還原動作，不是使用者本人的操作），且我們自己
 *      上一輪記下的 restoreRef 仍然是文件裡有效、非 dialog 的元素，就沿用舊的、
 *      不要被覆寫成不可信的值——避免焦點在多層巢狀切換後漂到 <body> 或漂進另一個
 *      已經消失的 dialog。
 *   2. 開啟時的預設焦點：偵測「這次開啟緊接在*另一個* Modal 的關閉之後（同一輪
 *      React 更新）」——這正是上述巢狀切換的訊號。這種情況下不用「聚焦 dialog
 *      本體」這個一般預設（那是給*全新*開啟用的，全新開啟時本體可能是刪除鈕，
 *      不該亂猜），改成聚焦頁尾（footer）裡最後一個**非危險**按鈕（跳過
 *      cui-btn-danger / cui-btn-danger-solid，理由與「刻意不自動聚焦第一個
 *      按鈕」相同——footer 找不到安全按鈕就照舊聚焦 dialog 本體，不會退化。
 *      這無法精準命中「使用者當初點的那顆按鈕」（如上所述物理上不可能），但能讓
 *      焦點落在同一個 dialog 裡「看起來合理」的位置，而不是每次都停在容器本身。
 */
let modalJustClosedFlag = false;
function markModalJustClosed(): void {
  modalJustClosedFlag = true;
  // 用巨集任務而非微任務重置：passive effect 的 flush 本身可能透過微任務排程，
  // 若用 Promise microtask 重置，不保證會晚於同一輪 commit 裡其他 Modal 的
  // setup effect 讀到旗標——setTimeout(0) 保證在那之後才清除。
  setTimeout(() => {
    modalJustClosedFlag = false;
  }, 0);
}

function pickResumeFocusTarget(dialog: HTMLElement): HTMLElement | null {
  const footer = dialog.querySelector<HTMLElement>(".cui-modal-footer");
  if (!footer) return null;
  const candidates = Array.from(footer.querySelectorAll<HTMLElement>(FOCUSABLE));
  for (let i = candidates.length - 1; i >= 0; i--) {
    const el = candidates[i];
    if (!el.classList.contains("cui-btn-danger") && !el.classList.contains("cui-btn-danger-solid")) {
      return el;
    }
  }
  return null;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  initialFocusRef,
  disableOverlayClose,
}: ModalProps) {
  useInjectStyles();
  const dialogRef = useRef<HTMLDivElement>(null);
  /** 開啟前的焦點所在，關閉後要還回去。 */
  const restoreRef = useRef<Element | null>(null);
  const titleId = useId();

  /*
   * 根治焦點被拉回的問題（useLatest 模式）。
   *
   * 呼叫端最常見的寫法是 inline 回呼——`<Modal onClose={() => setOpen(false)}>`、
   * `<ConfirmDialog onClose={busy ? () => {} : onCancel}>`——這種寫法每次 render
   * 都是新的函式參照。先前 focus effect 的依賴陣列包含 `onClose`：Modal 開啟中
   * 只要呼叫端所在的元件重繪一次（例如使用者在對話框裡打字造成 setState），
   * effect 就會被迫重跑一次——cleanup 先執行，被誤判成「我剛關閉」
   * （`markModalJustClosed()`），焦點被搶去 restoreRef／頁尾按鈕，使用者打到一半
   * 的輸入框就這樣被搶走焦點。course-info（9259cd1）、大體系（05f4162）、
   * ConfirmDialog 呼叫端都各自繞道過這個症狀（`useCallback` 固定參照 / `setTimeout
   * (…, 0)` 補聚焦），但那些都是治標——只要下一個呼叫端忘記包 `useCallback`，
   * 同一個 bug 就會重演一次。
   *
   * 根治：把 onClose 存進 ref，effect 本身只依賴 `open`。呼叫端傳什麼參照都不會
   * 讓 effect 重跑，只有「開/關」這個真正的狀態轉換才會——inline 回呼從此是安全的，
   * 不必再逐頁包 `useCallback`。上面三處既有的繞道留著不動（無害，只是多餘）。
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    // K3：見上方註解——只有在新抓到的 activeElement 看起來不可信（body、或
    // 落在某個 dialog 裡面，代表是別的 Modal 剛做的還原動作）、且我們自己上一輪
    // 記下的還原目標仍然有效時，才沿用舊值，否則正常抓新的。
    const candidate = document.activeElement;
    const looksUnreliable =
      candidate === null ||
      candidate === document.body ||
      (candidate instanceof Element && candidate.closest('[role="dialog"]') !== null);
    const previous = restoreRef.current;
    const previousStillValid =
      previous instanceof HTMLElement && document.contains(previous) && previous !== dialog;
    restoreRef.current = looksUnreliable && previousStillValid ? previous : candidate;

    // 這次開啟是不是緊接在另一個 Modal 關閉之後（同一輪 commit）——是的話代表
    // 巢狀切換，用「頁尾最後一個非危險按鈕」取代「聚焦 dialog 本體」這個
    // 一般預設；抓不到就照舊退回 dialog 本體，行為不變。
    const resumeTarget = modalJustClosedFlag ? pickResumeFocusTarget(dialog) : null;
    // 讀完就清掉：縮小「旗標還沒被 setTimeout 重置前，被完全不相干的下一次
    // 開啟誤讀」的視窗。
    modalJustClosedFlag = false;
    // 焦點進入對話框。優先用呼叫端指定的元素（確認類請指向「取消」），
    // 否則聚焦對話框本體 —— 刻意不自動聚焦第一個按鈕，因為破壞性操作的
    // 對話框裡第一個按鈕可能就是「刪除」，自動聚焦等於把危險動作放到 Enter 上。
    (initialFocusRef?.current ?? resumeTarget ?? dialog)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      // Focus trap：Tab 不得跑出對話框。
      // 沒有這道防護時，Tab 會跳到對話框後方被遮罩蓋住的頁面元素上，
      // 鍵盤與螢幕閱讀器使用者會「掉出」對話框卻不知道自己在哪。
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // 標記「我剛關閉」——同一輪 commit 裡若有另一個 Modal 緊接著開啟，
      // 它的 setup effect 會讀到這個旗標，判斷這是巢狀切換而非全新開啟。
      markModalJustClosed();
      // 焦點歸還觸發按鈕：關閉後焦點若留在 <body>，鍵盤使用者得從頭 Tab 一次
      // 才能回到原本的位置。
      const el = restoreRef.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- 見上方 useLatest 註解：onClose 讀 ref 不需入列；
  // initialFocusRef 是呼叫端傳入的 useRef 物件，本身參照穩定，只在這次開啟當下讀 `.current` 一次即可。

  if (!open) return null;

  // useId 而非固定字串：固定字串在「同時掛載兩個以上 Modal」時（真正巢狀，父層
  // 不卸載、子層疊上去的情境）會撞 id，兩個 dialog 的 aria-labelledby 都指到同一個
  // <h2>，其中一個的標題朗讀就是錯的。
  const labelId = `cui-modal-title-${titleId}`;

  return (
    <div
      className="cui-reset cui-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (disableOverlayClose) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="cui-modal"
        role="dialog"
        aria-modal="true"
        // 標題是字串時用 aria-labelledby 指向它（螢幕閱讀器會連同標題一起唸出，
        // 且標題若含格式化內容也能正確讀出）；不是字串時退回 aria-label。
        {...(typeof title === "string"
          ? { "aria-labelledby": labelId }
          : { "aria-label": typeof title === "string" ? title : undefined })}
        tabIndex={-1}
        style={width ? { maxWidth: width } : undefined}
      >
        {title !== undefined && (
          <div className="cui-modal-header">
            <h2 className="cui-modal-title" id={typeof title === "string" ? labelId : undefined}>
              {title}
            </h2>
            <button type="button" className="cui-modal-close" onClick={onClose} aria-label="關閉">
              ✕
            </button>
          </div>
        )}
        <div className="cui-modal-body">{children}</div>
        {footer !== undefined && <div className="cui-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
